import React from 'react';
import { View, ScrollView, TouchableOpacity, Text, Image, StyleSheet, type ViewStyle, type TextStyle } from 'react-native';

/**
 * 模块导航项的配置
 */
export interface ModuleNavItem {
  /** 唯一标识符 */
  key: string;
  /** 显示的标签文本 */
  label: string;
  /** 图标（emoji 或 Unicode 字符 或 图片路径） */
  icon: string;
  /** 是否为图片路径（如果为 true，icon 会被当作图片 URL 使用） */
  isImageIcon?: boolean;
  /** 点击回调 */
  onPress?: () => void;
  /** 是否禁用 */
  disabled?: boolean;
}

export interface ModuleNavBarProps {
  /** 导航项列表 */
  items: ModuleNavItem[];
  /** 选中的项的 key */
  activeKey?: string;
  /** 项被点击时的回调 */
  onPress?: (key: string, item: ModuleNavItem) => void;
  /** 是否允许横向滚动 */
  scrollable?: boolean;
  /** 自定义样式 */
  style?: ViewStyle;
  /** 项的自定义样式 */
  itemStyle?: ViewStyle;
  /** 标签的自定义文本样式 */
  labelStyle?: TextStyle;
  /** 活跃项的背景颜色 */
  activeBackgroundColor?: string;
  /** 活跃项的文本颜色 */
  activeTextColor?: string;
  /** 非活跃项的背景颜色 */
  inactiveBackgroundColor?: string;
  /** 非活跃项的文本颜色 */
  inactiveTextColor?: string;
}

/**
 * 模块导航栏组件
 * 
 * 用于展示多个功能模块（如旅行、酒店、交通等）
 * 可横向滚动或固定展示
 * 
 * @example
 * ```tsx
 * const items = [
 *   { key: 'travel', label: '旅行', icon: '✈️', onPress: () => navigate('/travel') },
 *   { key: 'hotel', label: '酒店', icon: '🏨', onPress: () => navigate('/hotel') },
 * ];
 * 
 * <ModuleNavBar items={items} scrollable activeKey="travel" />
 * ```
 */
const ModuleNavBar: React.FC<ModuleNavBarProps> = ({
  items,
  activeKey,
  onPress,
  scrollable = true,
  style,
  itemStyle,
  labelStyle,
  activeBackgroundColor = '#F3F4F6',
  activeTextColor = '#1F2937',
  inactiveBackgroundColor = 'transparent',
  inactiveTextColor = '#6B7280',
}) => {
  const renderItem = (item: ModuleNavItem) => {
    const isActive = activeKey === item.key;
    
    return (
      <TouchableOpacity
        key={item.key}
        style={[
          styles.item,
          {
            backgroundColor: isActive ? activeBackgroundColor : inactiveBackgroundColor,
          },
          itemStyle,
        ]}
        onPress={() => {
          item.onPress?.();
          onPress?.(item.key, item);
        }}
        disabled={item.disabled}
        activeOpacity={0.7}
      >
        {item.isImageIcon ? (
          <Image
            source={{ uri: item.icon }}
            style={styles.imageIcon}
            resizeMode="contain"
          />
        ) : (
          <Text style={[styles.icon, { fontSize: 32 }]}>
            {item.icon}
          </Text>
        )}
        <Text
          style={[
            styles.label,
            {
              color: isActive ? activeTextColor : inactiveTextColor,
            },
            labelStyle,
          ]}
          numberOfLines={1}
        >
          {item.label}
        </Text>
      </TouchableOpacity>
    );
  };

  if (scrollable) {
    return (
      <ScrollView
        style={[styles.container, style]}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        bounces={false}
      >
        {items.map(renderItem)}
      </ScrollView>
    );
  }

  return (
    <View style={[styles.container, styles.gridContainer, style]}>
      {items.map(renderItem)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  scrollContent: {
    paddingHorizontal: 0,
    gap: 8,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
  },
  item: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    minWidth: 70,
    gap: 4,
  },
  icon: {
    fontWeight: '500',
  },
  imageIcon: {
    width: 32,
    height: 32,
  },
  label: {
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
});

export default ModuleNavBar;
